<!--
 Copyright 2022 by domx Private Company

 All rights reserved,
 also regarding any disposal, exploitation, reproduction,
 editing, distribution, as well as in the event of
 applications for industrial property rights.

 This software contains proprietary and confidential
 information of domx Private Company.
 The disclosure of confidential information without
 express authorization is prohibited.

 This software may include Open Source Software.
 As far as Open Source Software is included,
 your rights for the respective Open Source Software
 components are governed by the applicable
 Open Source Software licenses.
-->

<template class="fill-height">
    <v-card
      flat
      class="secondary fill-height pa-12"
      height="25%">
        <v-form
          ref="form"
          lazy-validation
        >
        <v-subheader class="display-1 grayBold--text">
            {{$t("addPhotos")}}
        </v-subheader>
        <v-list class="secondary card-content" max-height="80vh" max-width="1000px">
        <v-list-item>
                <v-list-item-content class="grayBold--text">
                <v-list-item-title class="subtitle-2 grayBold--text">

                </v-list-item-title>
                <v-row style="width: 1000px;">
                    <v-col xs12 sm6 md6>
                        <v-file-input ref="photosInput" multiple counter @change="PreviewPhotos"
                        accept="image/*" :placeholder="$t('addPhotoPlaceHolder')" prepend-icon="mdi-camera"
                        :label="$t('photosStressed')" light :rules="photoRules" class="mr-5" @click:clear="clearPhotos">
                        <template v-slot:counter>
                          <span class="text-caption grey--text">
                            <span class="text-caption grey--text">
                                {{ $tc('photoCounterMsg', photoModel.photos?.length || 0, { count: photoModel.photos?.length || 0 }) }}
                            </span>
                          </span>
                        </template>
                        <!-- Vuetify calls selection once per file; only first slot shows the summary chip. -->
                        <template v-slot:selection="{ index }">
                          <v-chip
                            v-if="index === 0 && photoModel.photos.length"
                            small
                            outlined
                            color="primary"
                            class="ma-0"
                          >
                            {{ $tc('photoCounterMsg', photoModel.photos.length, { count: photoModel.photos.length }) }}
                          </v-chip>
                        </template>
                      </v-file-input>
                      <div v-if="photoModel.photos.length" class="mt-2">
                        <v-chip v-for="(f, i) in photoModel.photos" :key="i" class="ma-1" small outlined color="primary" close @click:close="removePhoto(i)" >
                          {{ f.name || `Photo ${i + 1}` }}
                        </v-chip>
                      </div>
                </v-col>
                <v-col xs12 sm6 md6>
                    <v-textarea light v-model="photoModel.notes" :label="$t('photosNotes')" auto-grow
                    append-icon="mdi-text-box-edit-outline" rows="1" :counter="50" :rules="photoNotesRule"></v-textarea>
                </v-col>
            </v-row>
            <v-container v-if="photoModel.previewUrls.length" class="d-flex justify-center">
                <v-carousel hide-delimiter-background height="300" hide-delimiters show-arrows>
                    <v-carousel-item v-for="(url, index) in photoModel.previewUrls" :key="url || index">
                        <v-img :src="url" contain class="d-flex align-center justify-center" style="max-height: 280px; object-fit: contain;" />
                    </v-carousel-item>
                </v-carousel>
            </v-container>
            </v-list-item-content>
        </v-list-item>
        </v-list>
        <v-card-actions>
        <v-spacer/>
        <v-btn
            depressed
            right
            light
            color="grayBold--text secondary"
            @click="hideModal()"
        >
            {{$t("cancel")}}
        </v-btn>
        <v-btn
            color="primary"
            depressed
            light
            right
            large
            class="modal-update"
            @click="doAddInstallationPhoto"
            :loading="isUploading || pendingCompression > 0"
            :disabled="pendingCompression > 0 || isUploading"
            ><v-icon left>mdi-plus</v-icon>{{$t("add")}}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
</template>

<script>
import { Component, Vue, Prop } from 'vue-property-decorator'
import { commonModule, installationsModule, partnersModule } from '@/store/modules/store-accessor'
import { deepTrim, installationPhotoCompressionSettings } from '@/helpers/data'
import imageCompression from 'browser-image-compression'

@Component
export default class AddPhotoToInstallationModal extends Vue {
    @Prop()
    payload;
    tab;

    photoModel = {
        add_photo: false,
        photos: [],
        notes: '',
        urls: [],
        previewUrls: [],
    };

    currentPhotos = []

    isUploading = false;
    disabled = false;

    pendingCompression = 0;

    async hideModal() {
        commonModule.hideModal();
    }

    revokeBlobUrl(url) {
        if (url && String(url).startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    }

    async PreviewPhotos(newPhotos) {
        if (!newPhotos) return;
        const newSelectedPhotos = Array.from(newPhotos);
        if (newSelectedPhotos.length === 0) return;

        const instantPreviews = newSelectedPhotos.map((file) => URL.createObjectURL(file));

        this.photoModel.photos = [...this.currentPhotos, ...newSelectedPhotos];
        this.photoModel.previewUrls = [
            ...this.photoModel.previewUrls,
            ...instantPreviews,
        ];
        this.currentPhotos = this.photoModel.photos;

        this.pendingCompression += newSelectedPhotos.length;
        const options = installationPhotoCompressionSettings();
        await Promise.all(
            newSelectedPhotos.map(async (originalFile) => {
                try {
                    const compressedPhoto = await imageCompression(originalFile, options);
                    const idx = this.photoModel.photos.indexOf(originalFile);
                    if (idx === -1) {
                        return;
                    }
                    this.$set(this.photoModel.photos, idx, compressedPhoto);
                } catch {
                    return;
                } finally {
                    this.pendingCompression = Math.max(0, this.pendingCompression - 1);
                }
            }),
        );
    }

    removePhoto(index) {
        this.revokeBlobUrl(this.photoModel.previewUrls[index]);
        this.photoModel.photos.splice(index, 1);
        this.photoModel.previewUrls.splice(index, 1);

        if (this.photoModel.photos.length === 0) {
            this.clearPhotos();
        }
    }

    clearPhotos() {
        for (const url of this.photoModel.previewUrls) {
            this.revokeBlobUrl(url);
        }
        this.photoModel.photos = [];
        this.photoModel.previewUrls = [];
        this.currentPhotos = [];
        this.pendingCompression = 0;

        if (this.$refs.photosInput) {
            this.$refs.photosInput.reset();

            const input = this.$refs.photosInput.$refs.input;
            if (input) input.value = null;
        }
    }

    photoRules = [
        (val) => val && val.length >= 1 || this.$t("photosRequired"),
        (val) => val && val.length <= 30 || this.$t('photoLengthRule'),
    ];
    photoNotesRule = [
        (val) => val.length <= 50 || this.$i18n.t("notesForPhotoRule"),
    ];

    async mounted() {
        this.tab = this.payload && this.payload.tab ? this.payload.tab : 'photos'
    }

    async doAddInstallationPhoto() {
        if (!this.$refs.form.validate()) {
            return;
        }
        if (this.pendingCompression > 0) {
            return;
        }

        this.isUploading = true;
        this.disabled = true;

        const formData = new FormData();
        for (const file of this.photoModel.photos) {
            formData.append('image', file);
        }

        this.photoModel.notes = deepTrim(this.photoModel.notes);
        formData.append('notes', this.photoModel.notes);

        switch (this.tab) {
            case 'photos':
                await installationsModule.addInstallationPhoto({
                    installationId: this.payload.installation.info.id,
                    photo: formData,
                });
                await installationsModule.getInstallationDetailsPhotos(this.payload.installation.info.id);
                await installationsModule.makeInstallationSideviewCommonCalls(String(this.payload.installation.info.id));
                break;
            case 'jobs':
                formData.append('job_id', this.payload.installation.actions[0].job_id);
                await installationsModule.addInstallationPhoto({
                    installationId: this.payload.installation.installation_id,
                    photo: formData,
                });
                if (
                    this.$route.name === 'My Company' &&
                    partnersModule?.currentPartner?.info?.partner_id
                ) {
                    partnersModule.getPartnersInfo(partnersModule.currentPartner.info.partner_id);
                } else {
                    await installationsModule.getInstallationDetailsJobs(this.payload.installation.installation_id,);
                    await installationsModule.makeInstallationSideviewCommonCalls(String(this.payload.installation.installation_id));
                    await installationsModule.invalidateInstallationTab({tabs: ['photos'], installationId: this.payload.installation.installation_id});
                }
                break;
        }

        this.hideModal();
    }
};
</script>
<style scoped lang="scss">
.card-content {
  overflow: auto;
}
</style>
